<template>
  <div class="relative overflow-hidden">
    <CheckoutStep class="h-full">
      <template #header>
        <Stepper :has-bouygues-steps="hasSelectedOnlineMobilePlan(items)" />
      </template>

      <template #aside>
        <CheckoutSummary
          :discount
          :discount-token
          :items="availableItems"
          :selected-payment-method="paymentMethod"
          :summary
          :swapOffer
          with-extended-legal
        >
          <CheckoutSummaryAddressesCard
            v-if="addressList.bill && addressList.deliver"
            :billing="addressList.bill"
            :shipping="
              addressList?.deliverCollectionPoint || addressList.deliver
            "
          />
        </CheckoutSummary>
      </template>

      <Payment
        :discount
        :items
        :mobile-plan-customer-information
        :payment-method
        :result-page-name="CHECKOUT.PAYMENT_RESULT"
        :summary
        :swapOffer
        @loaded="isPaymentLoading = false"
        @loading="handleStartLoading"
        @update="emit('update')"
      />
    </CheckoutStep>

    <RevLoadingScreen
      v-if="isPaymentLoading"
      class="bg-surface-default-mid fixed inset-0 z-10 w-full md:absolute md:right-[440px]"
      data-qa="loading-screen"
      :text="loadingMessage"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { useBouyguesStore } from '@backmarket/nuxt-layer-checkout/stores/bouyguesStore'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import Payment from '~/scopes/checkout/components/Payment/Payment.vue'
import { getMobilePlanCustomerInformation } from '~/scopes/checkout/components/Payment/utils/getMobilePlanCustomerInformation'
import { handleMobilePlanErrors } from '~/scopes/checkout/components/Payment/utils/handleMobilePlanErrors'
import { useRedirectBouyguesMobilePlan } from '~/scopes/checkout/composables/redirects/useRedirectBouyguesMobilePlan'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useRedirectIfMissingCollectionPoint } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingCollectionPoint'
import { useRedirectIfMissingShippingInformation } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingShippingInformation'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { hasSelectedOnlineMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import Stepper from '../../components/Stepper/Stepper.vue'

import CheckoutSummaryAddressesCard from './components/CheckoutSummaryAddressesCard/CheckoutSummaryAddressesCard.vue'

const props = defineProps<{
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'update'): void
}>()

const bouyguesStore = useBouyguesStore()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('PAYMENT')
const { redirectIfMissingShippingInformation } =
  useRedirectIfMissingShippingInformation('PAYMENT')
const { redirectIfMissingCollectionPoint } =
  useRedirectIfMissingCollectionPoint('PAYMENT')
const { redirectBouyguesMobilePlan } = useRedirectBouyguesMobilePlan()
const { trackFunnel } = useCheckoutTracking()

const isPaymentLoading = ref(false)
const loadingMessage = ref('')
const mobilePlanCustomerInformation = ref()

const availableItems = computed(() => getAvailableItems(props.items))

try {
  mobilePlanCustomerInformation.value = getMobilePlanCustomerInformation(
    hasSelectedOnlineMobilePlan(props.items),
    bouyguesStore.accessToken,
    bouyguesStore.phonelineSetupInformation,
  )?.customerInformation
} catch (error) {
  handleMobilePlanErrors(error as Error)
}

const handleStartLoading = (message: string) => {
  loadingMessage.value = message
  isPaymentLoading.value = true
}

onMounted(async () => {
  await redirectIfCartEmpty(props.items, CHECKOUT.CART)
  await redirectIfMissingShippingInformation(
    props.addressList,
    props.shippableCountries,
  )
  await redirectIfMissingCollectionPoint(
    props.items,
    props.shippableCountries,
    props.addressList,
    CHECKOUT.CART,
    props.lastCollectionPoint,
  )
  await redirectBouyguesMobilePlan(props.items)
  trackFunnel(props.items, props.swapOffer)
})
</script>
